import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { REGIONS, citiesIn } from "@/data/locations";
import { INDUSTRIES } from "@/data/industries";
import { BRAND_URL, Breadcrumbs, LinkGrid, CtaBand, breadcrumbLd } from "@/components/LocalSections";

const WRAP = "mx-auto max-w-[1360px] px-6 md:px-10";

export function LocationsHub() {
  const crumbs = [{ name: "Home", to: "/" }, { name: "Areas we serve", to: "/locations" }];
  return (
    <div data-testid="locations-hub">
      <PageHead
        title="Areas We Serve | Inland Empire & Orange County Web Design — Redkey"
        description="Redkey Web Design builds custom websites and local SEO for businesses across the Inland Empire (Riverside & San Bernardino Counties) and Orange County, CA."
        canonical={`${BRAND_URL}/locations`}
        jsonLd={{ "@context": "https://schema.org", ...breadcrumbLd(crumbs) }}
      />
      <Breadcrumbs items={crumbs} />
      <section className={`${WRAP} pt-10 md:pt-14 pb-12`}>
        <p className="eyebrow">§ Areas we serve</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-[20ch]">
          Web design for the Inland Empire <span className="text-brand-red">&amp;</span> Orange County.
        </h1>
        <p className="mt-6 max-w-prose text-lg text-ink-soft leading-relaxed">
          Custom, hand-coded websites and local SEO for Southern California businesses. Pick your area to see how we help local companies win more qualified leads.
        </p>
      </section>
      {REGIONS.map((r, i) => (
        <section key={r.slug} className={`border-t hairline ${i % 2 ? "" : "bg-paper-alt"}`} data-testid={`hub-region-${r.slug}`}>
          <div className={`${WRAP} py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10`}>
            <div className="md:col-span-5">
              <p className="eyebrow">{r.counties}</p>
              <h2 className="font-display text-3xl md:text-4xl mt-4">{r.name}</h2>
              <p className="mt-4 text-ink-soft leading-relaxed">{r.headline}</p>
              <Link to={`/web-design/${r.slug}`} className="inline-block mt-6 font-ui link-underline" data-testid={`hub-region-link-${r.slug}`}>
                {r.name} web design →
              </Link>
            </div>
            <ul className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3 content-start">
              {citiesIn(r.slug).map((c) => (
                <li key={c.slug}>
                  <Link to={`/web-design/${c.slug}`} data-testid={`hub-city-${c.slug}`}
                    className="block border hairline-strong bg-paper p-4 font-ui hover:border-brand-red transition-colors">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}
      <LinkGrid eyebrow="§ Industries" title="Websites built for your industry" testId="hub-industries"
        links={INDUSTRIES.map((i) => ({ to: `/industries/${i.slug}`, label: i.short }))} />
      <CtaBand text="Not on the list? We work across all of Southern California." testId="locations-hub" />
    </div>
  );
}

export function IndustriesHub() {
  const crumbs = [{ name: "Home", to: "/" }, { name: "Industries", to: "/industries" }];
  return (
    <div data-testid="industries-hub">
      <PageHead
        title="Industries We Serve | Websites for Contractors, Law Firms, Medical & More — Redkey"
        description="Custom websites for contractors, law firms, dental and medical practices, restaurants, home services, and real estate in the Inland Empire and Orange County."
        canonical={`${BRAND_URL}/industries`}
        jsonLd={{ "@context": "https://schema.org", ...breadcrumbLd(crumbs) }}
      />
      <Breadcrumbs items={crumbs} />
      <section className={`${WRAP} pt-10 md:pt-14 pb-12`}>
        <p className="eyebrow">§ Industries</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-[20ch]">
          Websites built around how <span className="text-brand-red">your</span> customers buy.
        </h1>
      </section>
      <section className="border-t hairline">
        <ul className={`${WRAP} py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`}>
          {INDUSTRIES.map((ind) => {
            const Icon = ind.Icon;
            return (
              <li key={ind.slug}>
                <Link to={`/industries/${ind.slug}`} data-testid={`hub-industry-${ind.slug}`}
                  className="group block h-full border hairline-strong p-6 hover:border-ink transition-colors">
                  <Icon size={22} strokeWidth={1.75} className="text-brand-red" aria-hidden />
                  <h2 className="font-display text-2xl mt-4 group-hover:text-brand-red transition-colors">{ind.name}</h2>
                  <p className="mt-3 text-ink-soft leading-relaxed">{ind.headline}</p>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
      <CtaBand text="Don't see your industry? Tell us what you do." testId="industries-hub" />
    </div>
  );
}
